export type RobotAssemblyPreset = 'battlebot' | 'battlebot-mini'

export type RobotCatalogEntry = {
  id: string
  name: string
  imageUrl: string
  intlKey: string | null
  assemblyPreset?: RobotAssemblyPreset
}

const robotImage = (filename: string) => `/assets/robot-models/model_${filename}.png`

/**
 * Finished-model images and Korean display names imported from PingPong Base.
 *
 * An entry only receives an assemblyPreset after its connector-by-connector
 * assembly has been verified in PingPongLand. Until then the card is visible,
 * searchable, and deliberately disabled instead of creating an incorrect robot.
 */
export const completedRobots: readonly RobotCatalogEntry[] = [
  { id: 'battlebot', name: '배틀봇', imageUrl: robotImage('battlebot'), intlKey: 'robot_battlebot', assemblyPreset: 'battlebot' },
  { id: 'minibattle', name: '배틀봇 미니', imageUrl: robotImage('minibattle'), intlKey: 'robot_minibattle', assemblyPreset: 'battlebot-mini' },
  { id: 'mono', name: '모노', imageUrl: robotImage('mono'), intlKey: 'robot_mono' },
  { id: 'autocar', name: '오토카', imageUrl: robotImage('autocar'), intlKey: 'robot_autocar' },
  { id: 'minicar', name: '미니카', imageUrl: robotImage('minicar'), intlKey: 'robot_minicar' },
  { id: 'drawingcar', name: '드로잉카', imageUrl: robotImage('drawingcar'), intlKey: 'robot_drawingcar' },
  { id: 'linebot', name: '라인봇', imageUrl: robotImage('linebot'), intlKey: 'robot_linebot' },
  { id: 'tankbot', name: '탱크봇', imageUrl: robotImage('tankbot'), intlKey: 'robot_tankbot' },
  { id: 'crabbot', name: '크랩봇', imageUrl: robotImage('crabbot'), intlKey: 'robot_crabbot' },
  { id: 'sealbot', name: '씰봇', imageUrl: robotImage('sealbot'), intlKey: 'robot_sealbot' },
  { id: 'acrobatbot', name: '아크로뱃봇', imageUrl: robotImage('acrobatbot'), intlKey: 'robot_acrobatbot' },
  { id: 'cleanerbot', name: '청소기로봇', imageUrl: robotImage('cleanerbot'), intlKey: 'robot_cleanerbot' },
  { id: 'rollingcar', name: '롤링카', imageUrl: robotImage('rollingcar'), intlKey: 'robot_rollingcar' },
  { id: 'wormbot', name: '웜봇', imageUrl: robotImage('wormbot'), intlKey: 'robot_wormbot' },
  { id: 'duckbot', name: '덕봇', imageUrl: robotImage('duckbot'), intlKey: 'robot_duckbot' },
  { id: 'drawingbot', name: '드로잉봇', imageUrl: robotImage('drawingbot'), intlKey: 'robot_drawingbot' },
  { id: 'antbot', name: '앤트봇', imageUrl: robotImage('antbot'), intlKey: 'robot_antbot' },
  { id: 'miniant', name: '미니앤트봇', imageUrl: robotImage('miniant'), intlKey: 'robot_miniant' },
  { id: 'twistcar', name: '트위스트카', imageUrl: robotImage('twistcar'), intlKey: 'robot_twistcar' },
  { id: 'robotarm3', name: '로봇팔(G3)', imageUrl: robotImage('robotarm3'), intlKey: 'robot_robotarm' },
  { id: 'humanbot', name: '휴먼봇', imageUrl: robotImage('humanbot'), intlKey: 'robot_humanbot' },
  { id: 'shootingbot', name: '슈팅봇', imageUrl: robotImage('shootingbot'), intlKey: 'robot_shootingbot' },
  { id: 'scarabot', name: '스칼라봇', imageUrl: robotImage('scarabot'), intlKey: 'robot_scarabot' },
  { id: 'jointcar', name: '조인트카', imageUrl: robotImage('jointcar'), intlKey: 'robot_jointcar' },
  { id: 'robotarm', name: '로봇팔', imageUrl: robotImage('robotarm'), intlKey: 'robot_robotarm' },
  { id: 'twincar', name: '트윈카', imageUrl: robotImage('twincar'), intlKey: 'robot_twincar' },
  { id: 'omnibot', name: '옴니봇', imageUrl: robotImage('omnibot'), intlKey: 'robot_omnibot' },
  { id: 'mekabot', name: '메카봇', imageUrl: robotImage('mekabot'), intlKey: 'robot_mekabot' },
  { id: 'dancingbot', name: '댄싱봇', imageUrl: robotImage('dancingbot'), intlKey: 'robot_dancingbot' },
  { id: 'crawlingbot', name: '크롤링봇', imageUrl: robotImage('crawlingbot'), intlKey: 'robot_crawlingbot' },
  { id: 'twistbot-four', name: '트위스트봇(G4)', imageUrl: robotImage('twistbot_four'), intlKey: 'robot_twistbot4' },
  { id: 'skidbot', name: '스키드봇', imageUrl: robotImage('skidbot'), intlKey: 'robot_skidbot' },
  { id: 'horsebot', name: '홀스봇', imageUrl: robotImage('horsebot'), intlKey: 'robot_horsebot' },
  { id: 'quadbot', name: '쿼드봇', imageUrl: robotImage('quadbot'), intlKey: 'robot_quadbot' },
  { id: 'smarttank', name: '스마트탱크', imageUrl: robotImage('smarttank'), intlKey: 'robot_smarttank' },
  { id: 'bridgebot', name: '브릿지봇', imageUrl: robotImage('bridgebot'), intlKey: 'robot_bridgebot' },
  { id: 'snakebot', name: '스네이크봇', imageUrl: robotImage('snakebot'), intlKey: 'robot_snakebot' },
  { id: 'hexbot', name: '헥스봇', imageUrl: robotImage('hexbot'), intlKey: 'robot_hexbot' },
  { id: 'twistbot-six', name: '트위스트봇(G6)', imageUrl: robotImage('twistbotlong'), intlKey: 'robot_twistbot6' },
  { id: 'bipedbot', name: '바이프드봇', imageUrl: robotImage('bipedbot'), intlKey: 'robot_bipedbot' },
  // This legacy image has no matching key in the current intl catalog.
  { id: 'balancing', name: '밸런싱봇', imageUrl: robotImage('balancing'), intlKey: null },
]
